/**
 * Coleta de posição do navegador — compartilhada entre o portal do notificado
 * (que a usa no tracking do aceite) e a aba Auditoria da Secretaria (que manda
 * a coordenada no header `X-Client-Geo` para o servidor resolver cidade/UF).
 *
 * Promovida de `features/portal/utils/tracking.ts` quando ganhou o segundo
 * consumidor: duas cópias da mesma chamada divergiriam nos parâmetros que
 * importam (timeout, `maximumAge`) e a auditoria passaria a registrar posições
 * com precisão diferente da do portal, sem que ninguém tivesse decidido isso.
 */

import type { PosicaoNavegador } from '@/shared/types/contextoAuditoria';

export type { PosicaoNavegador };

/** Timeout e cache da leitura. Valores herdados do portal — não os divirja sem motivo. */
const TIMEOUT_MS = 10_000;
const IDADE_MAXIMA_MS = 300_000;

/**
 * Posição atual, ou `null`.
 *
 * **Sempre resolve, nunca rejeita.** Permissão negada é o caso comum, não uma
 * falha: quem chama trata a ausência como "sem geolocalização" e segue. Também
 * devolve `null` no servidor (SSR) e quando a API não existe.
 *
 * `enableHighAccuracy: false` de propósito — acionar o GPS custa bateria e
 * segundos para uma precisão que nenhum dos dois consumidores usa (o destino é
 * cidade/UF).
 */
export async function coletarPosicaoNavegador(): Promise<PosicaoNavegador | null> {
  if (typeof window === 'undefined' || !navigator.geolocation) {
    return null;
  }

  return new Promise((resolve) => {
    // Timeout próprio além do `options.timeout`: navegador que nunca chama
    // nenhum dos dois callbacks (permissão pendente e aba em background)
    // deixaria a Promise pendurada para sempre.
    const timeout = setTimeout(() => resolve(null), TIMEOUT_MS);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeout);
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      () => {
        clearTimeout(timeout);
        resolve(null);
      },
      {
        enableHighAccuracy: false,
        timeout: TIMEOUT_MS,
        maximumAge: IDADE_MAXIMA_MS,
      },
    );
  });
}

/** Formato `"<lat>,<long>"` do header `X-Client-Geo`. */
export function paraHeaderGeo(posicao: PosicaoNavegador | null): string | undefined {
  return posicao ? `${posicao.latitude},${posicao.longitude}` : undefined;
}
